import ResponsiveEditDialog from "@/modules/common/components/modals/responsive-edit-dialog";
import { Table } from "@tanstack/react-table";
import { Box, Button, Switch } from "@mui/material";
import { muiTheme } from "@/styles/theme";

const switchStyles = {
  width: 36,
  height: 20,
  padding: "0px",
  "& .MuiSwitch-switchBase": {
    padding: "2px",
    "&.Mui-checked": {
      transform: "translateX(16px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#006694",
        opacity: 1,
        border: 0,
      },
      "&.Mui-focusVisible .MuiSwitch-thumb": {
        color: "#006694",
        border: "6px solid #fff",
      },
    },
    "&:not(.Mui-checked)": {
      transform: "translateX(0)",
    },
  },
  "& .MuiSwitch-thumb": {
    width: 16,
    height: 16,
  },
  "& .MuiSwitch-track": {
    borderRadius: 26 / 2,
    backgroundColor: "#E9E9EA",
    opacity: 1,
    transition: muiTheme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
};

interface ColumnVisibilityDialogProps<TData> {
  open: boolean;
  setOpen: (open: boolean) => void;
  table: Table<TData>;
  defaultColumns?: string[];
}

export default function ColumnVisibilityDialog<TData>({
  open,
  setOpen,
  table,
  defaultColumns = [],
}: ColumnVisibilityDialogProps<TData>) {
  const { getAllLeafColumns } = table;

  // Function to restore default column visibility
  const handleRestoreDefault = () => {
    const allColumns = getAllLeafColumns();

    // Create a new visibility state
    const newVisibilityState: Record<string, boolean> = {};

    // Set all columns to hidden by default
    allColumns.forEach((column) => {
      if (column.getCanHide()) {
        newVisibilityState[column.id] = false;
      }
    });

    // Set default columns to visible
    defaultColumns.forEach((columnId) => {
      newVisibilityState[columnId] = true;
    });

    // Update the table's column visibility state
    table.setColumnVisibility(newVisibilityState);
  };

  return (
    <ResponsiveEditDialog
      open={open}
      setOpen={setOpen}
      width={400}
      header="Column Visibility"
      modalFooter={false}
      hideModalFooter={true}
    >
      <div
        style={{
          display: "grid",
          flexDirection: "column",
          gap: "16px",
          maxHeight: "800px",
        }}
      >
        {getAllLeafColumns().map((column) => {
          if (!column.getCanHide()) {
            return null;
          }
          return (
            <div
              key={column.id}
              style={{
                display: "flex",
                gap: "8px",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <Switch
                checked={column.getIsVisible()}
                onChange={(e) => {
                  e.stopPropagation();
                  column.toggleVisibility();
                }}
                onClick={(e) => e.stopPropagation()}
                color="primary"
                sx={switchStyles}
              />
              <div>{column.columnDef.header?.toString()}</div>
            </div>
          );
        })}

        <Box
          sx={{
            flexGrow: 1,
            padding: 2,
            minWidth: "100%",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Button
            onClick={handleRestoreDefault}
            variant="outlined"
            color="primary"
            sx={{ alignSelf: "center", textTransform: "none" }}
          >
            Restore Default
          </Button>
        </Box>
      </div>
    </ResponsiveEditDialog>
  );
}
